import type { components } from '@/api/schema';

import { keysOf, labelOf, optionsOf } from './humanize';

type Schemas = components['schemas'];

export type JobStatus = Schemas['Job']['status'];
export type JobPriority = Schemas['Job']['priority'];
export type AppointmentClass = Schemas['Job']['appointmentClass'];

// The technician's phone and the dispatch board name a status the same way: a driver says "on the
// way" and "finished", so the office does too.
export const JOB_STATUS_LABELS: Record<JobStatus, string> = {
  SCHEDULED: 'Scheduled',
  DISPATCHED: 'Dispatched',
  EN_ROUTE: 'On the way',
  ON_SITE: 'On site',
  COMPLETED: 'Finished',
  ON_HOLD: 'On hold',
  CANCELLED: 'Cancelled',
};

export const JOB_PRIORITY_LABELS: Record<JobPriority, string> = {
  LOW: 'Low',
  NORMAL: 'Normal',
  HIGH: 'High',
  URGENT: 'Urgent',
};

// Service work is a "job"; the sales appointment is a "sales visit". Nothing else is a call, an
// appointment or a service call.
export const APPOINTMENT_CLASS_LABELS: Record<AppointmentClass, string> = {
  SERVICE: 'Service job',
  SALES: 'Sales visit',
};

/** What a dispatch lane takes: one kind of booking or both; the schema leaves it a plain string. */
export type LaneClass = AppointmentClass | 'COMBINED';

export const LANE_CLASS_LABELS: Record<LaneClass, string> = {
  SERVICE: 'Service jobs',
  SALES: 'Sales visits',
  COMBINED: 'Service jobs and sales visits',
};

/** How often a recurring visit repeats; the schema leaves the frequency an open string. */
export type RecurrenceFrequency = 'DAILY' | 'WEEKLY' | 'MONTHLY';

export const RECURRENCE_FREQUENCY_LABELS: Record<RecurrenceFrequency, string> = {
  DAILY: 'Daily',
  WEEKLY: 'Weekly',
  MONTHLY: 'Monthly',
};

/** Every job status, in lifecycle order. */
export const JOB_STATUSES = keysOf(JOB_STATUS_LABELS);

/** Every job priority, lowest first. */
export const JOB_PRIORITIES = keysOf(JOB_PRIORITY_LABELS);

/** The two kinds of booking a job can be, service work first. */
export const APPOINTMENT_CLASSES = keysOf(APPOINTMENT_CLASS_LABELS);

/** The ways a visit can repeat, as `{ value, label }`, for a select. */
export const RECURRENCE_FREQUENCY_OPTIONS = optionsOf(RECURRENCE_FREQUENCY_LABELS);

export function jobStatusLabel(status: string | null | undefined): string {
  return labelOf(JOB_STATUS_LABELS, status);
}

export function jobPriorityLabel(priority: string | null | undefined): string {
  return labelOf(JOB_PRIORITY_LABELS, priority);
}

export function appointmentClassLabel(appointmentClass: string | null | undefined): string {
  return labelOf(APPOINTMENT_CLASS_LABELS, appointmentClass);
}

export function laneClassLabel(laneClass: string | null | undefined): string {
  return labelOf(LANE_CLASS_LABELS, laneClass);
}

export function recurrenceFrequencyLabel(frequency: string | null | undefined): string {
  return labelOf(RECURRENCE_FREQUENCY_LABELS, frequency);
}
